// src/store/modules/i18n.js
import apiClient from '@/api/index';
import { setI18nLocale } from '@/api/i18n';
import {
    DEFAULT_LOCALE,
    normalizeLocale,
    resolvePreferredLocale,
} from '@/utils/locale';

const localeLoadPromises = new Map();

const state = {
    locale: resolvePreferredLocale(),
    messages: {},
};

const mutations = {
    setLocale(state, { locale, messages }) {
        state.locale = locale;
        state.messages = { ...state.messages, [locale]: messages };
        localStorage.setItem('locale', locale);
    },
};

const actions = {
    applyLocale({ commit }, { locale, messages }) {
        const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;
        setI18nLocale(normalizedLocale, messages);
        commit('setLocale', { locale: normalizedLocale, messages });
        return messages;
    },
    async loadLocale({ dispatch }, locale = DEFAULT_LOCALE) {
        const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;

        if (localeLoadPromises.has(normalizedLocale)) {
            return localeLoadPromises.get(normalizedLocale);
        }

        const loadPromise = apiClient
            .get(`/languages/lang/${normalizedLocale}/?disable_cache=false`, {
                skipAuthorization: true,
            })
            .then(response =>
                dispatch('applyLocale', {
                    locale: normalizedLocale,
                    messages: response.data,
                })
            )
            .catch(error => {
                if (normalizedLocale !== DEFAULT_LOCALE) {
                    console.warn(
                        `Failed to load locale "${normalizedLocale}", falling back to "${DEFAULT_LOCALE}".`
                    );
                    return dispatch('loadLocale', DEFAULT_LOCALE);
                }

                throw error;
            })
            .finally(() => {
                localeLoadPromises.delete(normalizedLocale);
            });

        localeLoadPromises.set(normalizedLocale, loadPromise);
        return loadPromise;
    },
    async ensureLocaleLoaded({ state, dispatch }, locale = state.locale) {
        const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;

        if (state.messages[normalizedLocale]) {
            return dispatch('applyLocale', {
                locale: normalizedLocale,
                messages: state.messages[normalizedLocale],
            });
        }

        return dispatch('loadLocale', normalizedLocale);
    },
};

const getters = {
    locale: state => state.locale,
    messages: state => state.messages,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
